/** Framework-free URL grammar for a root collection. Browser binding belongs to the host. */

export interface GridUrl {
  view?: string
  q?: string
  sort?: string
  f?: string
}

export const GRID_URL_KEYS = ['view', 'q', 'sort', 'f'] as const

/** The grid keys of a raw search object: strings only, empties dropped. */
export function gridSearchParams(search: Record<string, unknown>): GridUrl {
  const out: GridUrl = {}
  for (const key of GRID_URL_KEYS) {
    const value = search[key]
    if (typeof value === 'string' && value.length > 0) out[key] = value
  }
  return out
}

/**
 * Extend a host route's validator without making this model depend on its
 * router. Unknown keys remain the host validator's decision.
 */
export function withGridSearch<T extends object>(
  validate: (search: Record<string, unknown>) => T,
): (search: Record<string, unknown>) => T & GridUrl {
  return (search) => ({ ...validate(search), ...gridSearchParams(search) })
}

export interface SortTerm {
  key: string
  direction: 'asc' | 'desc'
}

/** `name,-kind` ⇄ ordered sort terms. */
export function encodeSortParam(terms: readonly SortTerm[]): string | undefined {
  if (terms.length === 0) return undefined
  return terms.map((term) => (term.direction === 'desc' ? '-' : '') + term.key).join(',')
}

export function decodeSortParam(value: string | undefined): SortTerm[] {
  if (!value) return []
  return value.split(',').map((part) => part.trim()).filter(Boolean).map((part) => part.startsWith('-')
    ? { key: part.slice(1), direction: 'desc' as const }
    : { key: part, direction: 'asc' as const }).filter((term) => term.key.length > 0)
}

/**
 * The URL grammar owns only the stable filter envelope. A host's richer leaf
 * vocabulary remains structural and does not become an Experience dependency.
 */
export interface UrlFilterLeaf {
  op?: string
}

export function encodeFilterParam<F extends UrlFilterLeaf>(leaves: readonly F[]): string | undefined {
  return leaves.length === 0 ? undefined : JSON.stringify(leaves)
}

/** A malformed `f` is ignored as one value, never partially applied. */
export function decodeFilterParam<F extends UrlFilterLeaf = UrlFilterLeaf>(
  value: string | undefined,
): F[] | null {
  if (!value) return []
  try {
    const parsed: unknown = JSON.parse(value)
    if (!Array.isArray(parsed)) return null
    const valid = parsed.every((leaf) => leaf && typeof leaf === 'object' && !Array.isArray(leaf)
      && typeof (leaf as { op?: unknown }).op === 'string')
    return valid ? parsed as F[] : null
  } catch {
    return null
  }
}

/** The canonical grid URL; values equal to the saved view are omitted. */
export function canonicalGridUrl<F extends UrlFilterLeaf>(state: {
  viewId: string | null
  defaultViewId: string | null
  q: string
  sort: readonly SortTerm[]
  savedSort: readonly SortTerm[]
  filters: readonly F[]
  savedFilters: readonly F[]
}): GridUrl {
  const out: GridUrl = {}
  if (state.viewId && state.viewId !== state.defaultViewId) out.view = state.viewId
  const q = state.q.trim()
  if (q) out.q = q
  const sort = encodeSortParam(state.sort)
  if (sort !== encodeSortParam(state.savedSort) && sort) out.sort = sort
  const f = encodeFilterParam(state.filters)
  if (f !== encodeFilterParam(state.savedFilters) && f) out.f = f
  return out
}

export const sameGridUrl = (left: GridUrl, right: GridUrl): boolean =>
  GRID_URL_KEYS.every((key) => (left[key] ?? '') === (right[key] ?? ''))
